import type { StrictPaddingStyle } from "../util/types";

import { Text } from "../Text";
import { type Theme, useTheme } from "../ThemeProvider";

import React, { type ReactNode } from "react";
import { View, type ViewProps, StyleSheet, type ViewStyle, type TextStyle } from "react-native";

export interface BadgeProps extends Omit<ViewProps, "children" | "style"> {
  // Omit children from ViewProps
  /**
   * Value displayed inside the badge (e.g., "1", "99+"). If value is empty or no value is given an empty dot will be shown.
   */
  value?: ReactNode;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Overrides the text that's read by the screen reader when the user interacts with the element.
   * Consumers should provide a descriptive label (e.g., "1 new notification", "99+ unread messages").
   */
  accessibilityLabel?: string;
}

export function Badge({
  value = undefined,
  sx,
  accessibilityLabel,
  ...rest
}: BadgeProps): React.JSX.Element {
  const theme = useTheme();

  const badgeStyle = value ? styles.regularButton : styles.dotButton;
  const baseContainerStyle = React.useMemo(() => getBaseContainerStyle(theme), [theme]);
  const baseTextStyle = React.useMemo(() => getBaseTextStyle(theme), [theme]);
  const style = StyleSheet.flatten([baseContainerStyle, sx, badgeStyle]);

  return (
    <View style={style} accessible={true} accessibilityLabel={accessibilityLabel} {...rest}>
      <Text sx={baseTextStyle} ariaHidden={!!accessibilityLabel}>
        {value}
      </Text>
    </View>
  );
}

const getBaseContainerStyle = (theme: Theme): ViewStyle => {
  return {
    paddingHorizontal: theme.spacing.s1,
    borderRadius: theme.borderRadius.full,
    backgroundColor: theme.colors.foregroundAccent,
    borderWidth: 1,
    borderColor: theme.colors.foregroundInverse,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  };
};

const getBaseTextStyle = (theme: Theme): TextStyle => {
  return {
    fontSize: theme.fontSizes.f1,
    fontWeight: "bold",
    color: theme.colors.foregroundInverse,
    textAlign: "center",
    includeFontPadding: false,
  };
};

const styles = StyleSheet.create({
  regularButton: {
    minWidth: 18,
    minHeight: 18,
  },
  dotButton: {
    width: 12,
    height: 12,
  },
});
